/**
 * Navigation direction tracking for View Transitions API.
 * Determines whether a navigation is "forward" (deeper into the app)
 * or "back" (returning to a parent level), which drives the CSS
 * slide direction on mobile.
 */

// Route depth map: lower numbers = higher level (closer to "home")
// Routes at the same depth get 'forward' when navigating between them
const routeDepth: Record<string, number> = {
    '/': -1,             // login (outside app)
    '/registration': -1, // registration (outside app)
    '/reset-password': -1,
    '/map': 0,           // home base
    '/history': 0,       // same level as map (shared MapLayout)
    '/pets': 1,
    '/trackers': 1,
    '/contacts': 1,
    '/events': 1,
    '/profile': 1,
    '/payment': 1,
    '/contactForm': 2,
    '/addTracker': 2,
    '/basic': 2,
}

// Route pairs that should never animate (they share the same layout wrapper)
const noTransitionPairs: [string, string][] = [
    ['/map', '/history'],
]

/**
 * Determines navigation direction based on route depth.
 * - 'forward': navigating deeper (slide left on mobile)
 * - 'back': navigating to a shallower level (slide right on mobile)
 * - 'none': no transition (e.g., map ↔ history, or login transitions)
 */
export function getNavDirection(from: string, to: string): 'forward' | 'back' | 'none' {
    // Same route — no transition
    if (from === to) return 'none'

    // Check no-transition pairs
    for (const [a, b] of noTransitionPairs) {
        if ((from === a && to === b) || (from === b && to === a)) {
            return 'none'
        }
    }

    const fromDepth = routeDepth[from] ?? 1
    const toDepth = routeDepth[to] ?? 1

    // Navigating from outside the app (login) — use 'none' for a fade
    if (fromDepth === -1 || toDepth === -1) return 'none'

    if (toDepth > fromDepth) return 'forward'
    if (toDepth < fromDepth) return 'back'

    // Same depth (e.g., /pets → /contacts via menu) — treat as forward
    return 'forward'
}

/**
 * Extracts the base path from a full pathname for depth lookup.
 * Handles cases like '/pets?id=123' or future nested routes.
 */
export function getBasePath(pathname: string): string {
    // Strip query params and hash
    const clean = pathname.split('?')[0].split('#')[0]
    // Return the path as-is (our routes are all top-level)
    return clean
}
